import { BadgeCheck, Check, MapPin, Pencil, Settings, Share2, Users, UserCheck } from 'lucide-react';
import { formatCount } from '../../backend/mediaService';

interface ProfileHeaderProps {
  name: string;
  username: string;
  avatar?: string;
  bio?: string;
  location?: string;
  email?: string;
  isOfficial: boolean;
  isSelf: boolean;
  isFollowing: boolean;
  followingDuration?: string;
  isBlocked: boolean;
  followerCount: number;
  followingCount: number;
  views: number;
  likes: number;
  copied: boolean;
  onShare: () => void;
  onEditAvatar?: () => void;
  avatarBusy?: boolean;
  onSettings: () => void;
  onCreators: () => void;
  onFollow: () => void;
  onBlock: () => void;
  onSubscribers: () => void;
  onFollowing?: () => void;
}

export function ProfileHeader({
  name,
  username,
  avatar,
  bio,
  location,
  email,
  isOfficial,
  isSelf,
  isFollowing,
  followingDuration,
  isBlocked,
  followerCount,
  followingCount,
  views,
  likes,
  copied,
  onShare,
  onEditAvatar,
  avatarBusy = false,
  onSettings,
  onCreators,
  onFollow,
  onBlock,
  onSubscribers,
  onFollowing
}: ProfileHeaderProps) {
  const stats = [
    { label: 'Followers', value: followerCount, onClick: onSubscribers },
    { label: 'Following', value: followingCount, onClick: isSelf ? onFollowing : undefined },
    { label: 'Views', value: views },
    { label: 'Likes', value: likes }
  ];

  return (
    <header className="rounded-b-3xl border-b border-slate-200 bg-white">
      <div className="relative h-32 bg-gradient-to-r from-indigo-950 via-blue-800 to-cyan-500">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_20%,rgba(255,255,255,.35),transparent_35%)]" />
        <div className="absolute right-3 top-3 flex gap-2">
          <button type="button" aria-label="Share profile" onClick={onShare} className="rounded-xl border border-white/30 bg-black/15 p-2.5 text-white backdrop-blur hover:bg-black/25">
            {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
          </button>
          {isSelf && <>
            <button type="button" aria-label="Browse creators" onClick={onCreators} className="rounded-xl border border-white/30 bg-black/15 p-2.5 text-white backdrop-blur hover:bg-black/25">
              <Users className="h-4 w-4" />
            </button>
            <button type="button" aria-label="Profile settings" onClick={onSettings} className="rounded-xl border border-white/30 bg-black/15 p-2.5 text-white backdrop-blur hover:bg-black/25">
              <Settings className="h-4 w-4" />
            </button>
          </>}
        </div>
      </div>

      <div className="px-4 pb-4 sm:px-6">
        <div className="-mt-12 flex items-end justify-between gap-3 sm:-mt-14">
          <div className="relative z-10 flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-3xl border-4 border-white ring-4 ring-white bg-gradient-to-br from-slate-200 to-blue-100 shadow-lg sm:h-28 sm:w-28">
            {avatar && <img src={avatar} alt="" className="h-full w-full object-cover" />}
            {isSelf && onEditAvatar && (
              <button
                type="button"
                aria-label="Change profile photo"
                title="Change profile photo"
                disabled={avatarBusy}
                onClick={onEditAvatar}
                className="absolute bottom-1 right-1 grid h-8 w-8 place-items-center rounded-full border-2 border-white bg-blue-950 text-white shadow-md disabled:opacity-60"
              >
                {avatarBusy
                  ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  : <Pencil className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
          {!isSelf && <div className="mb-1 flex items-end gap-2">
            <div className="flex flex-col items-center gap-1">
              <button type="button" onClick={onFollow} disabled={isBlocked} aria-label={isFollowing ? 'Unfollow this account' : 'Follow this account'} className="flex min-h-10 items-center gap-1 rounded-xl bg-blue-950 px-4 text-xs font-bold text-white disabled:bg-slate-200 disabled:text-slate-500">
                {isFollowing ? <><UserCheck className="h-4 w-4" /> Following</> : 'Follow'}
              </button>
              {isFollowing && followingDuration && <span className="text-[9px] font-medium text-slate-500">Following · {followingDuration}</span>}
            </div>
            <button type="button" onClick={onBlock} className="min-h-10 rounded-xl border border-rose-200 px-3 text-xs font-semibold text-rose-700">{isBlocked ? 'Unblock' : 'Block'}</button>
          </div>}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <h1 className="text-xl font-extrabold tracking-tight text-slate-950">{name}</h1>
          {isOfficial && (
            <BadgeCheck aria-label="Official" title="Official account" className="h-5 w-5 fill-orange-600 text-white" />
          )}
        </div>
        <p className="mt-0.5 text-sm font-semibold text-blue-700">@{username}</p>
        {bio && <p className="mt-3 max-w-2xl whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{bio}</p>}
        {(location || (isSelf && email)) && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            {location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{location}</span>}
            {isSelf && email && <span>{email}</span>}
          </div>
        )}

        <div className="mt-5 grid grid-cols-4 divide-x divide-slate-200 border-y border-slate-100 py-3">
          {stats.map(stat => (
            <button key={stat.label} type="button" onClick={stat.onClick} disabled={!stat.onClick} className="flex min-w-0 flex-col items-center px-1 text-center disabled:cursor-default">
              <span className="text-base font-extrabold tabular-nums text-slate-950 sm:text-lg">{formatCount(stat.value)}</span>
              <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">{stat.label}</span>
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
